import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Save, Printer, ClipboardList, Upload, FileText, CheckCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function NewAssessmentPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patientId || '');
  const [selectedTherapist, setSelectedTherapist] = useState(user?.id || '');
  const [assessmentType, setAssessmentType] = useState('');
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  
  // Document upload state - for uploading completed assessment docs
  const [uploadMode, setUploadMode] = useState('form'); // 'form' or 'upload'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (assessmentType) {
      fetchTemplate();
    }
  }, [assessmentType]);

  const fetchData = async () => {
    try {
      const [patientsRes, therapistsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/staff/therapists')
      ]);
      setPatients(patientsRes.data);
      setTherapists(therapistsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await api.get(`/assessments/templates/${assessmentType}`);
      setTemplate(response.data);
      
      // Initialize form data with empty values
      const initialData = {};
      response.data.sections?.forEach(section => {
        section.fields?.forEach(field => {
          if (typeof field === 'string') {
            initialData[field] = '';
          } else {
            initialData[field.name] = field.type === 'checkbox' ? false : 
                                      field.type === 'checkbox_group' ? [] : '';
          }
        });
      });
      setFormData(initialData);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      toast.error('Failed to load assessment template');
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleCheckboxGroupChange = (fieldName, option, checked) => {
    setFormData(prev => {
      const current = prev[fieldName] || [];
      if (checked) {
        return { ...prev, [fieldName]: [...current, option] };
      } else {
        return { ...prev, [fieldName]: current.filter(o => o !== option) };
      }
    });
  };

  // Handle document file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setUploadedFile(file);
    }
  };

  // Submit with uploaded document
  const handleUploadSubmit = async () => {
    if (!selectedPatient || !assessmentType || !selectedTherapist || !uploadedFile) {
      toast.error('Please fill in all required fields and select a document');
      return;
    }

    setUploading(true);
    try {
      // First create the assessment record
      const assessmentRes = await api.post('/assessments', {
        patient_id: selectedPatient,
        therapist_id: selectedTherapist,
        assessment_type: assessmentType,
        date: new Date().toISOString().split('T')[0],
        form_data: {},
        status: 'completed',
        has_uploaded_document: true
      });

      const assessmentId = assessmentRes.data.id;

      // Then upload the document
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('entity_type', 'assessment');
      formData.append('entity_id', assessmentId);
      formData.append('category', 'assessment_document');
      formData.append('description', `${assessmentType.replace(/_/g, ' ').toUpperCase()} - Uploaded Document`);

      await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Assessment created with uploaded document - marked as COMPLETE');
      navigate('/assessments');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assessment');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (status = 'draft') => {
    if (!selectedPatient || !assessmentType || !selectedTherapist) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/assessments', {
        patient_id: selectedPatient,
        therapist_id: selectedTherapist,
        assessment_type: assessmentType,
        date: new Date().toISOString().split('T')[0],
        form_data: formData,
        status: status,
        has_uploaded_document: false
      });
      toast.success(`Assessment ${status === 'draft' ? 'saved as draft' : 'submitted'} successfully`);
      navigate('/assessments');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save assessment');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (typeof field === 'string') {
      return (
        <div key={field} className="space-y-2">
          <Label>{field.replace(/_/g, ' ')}</Label>
          <Input
            value={formData[field] || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
          />
        </div>
      );
    }

    const { name, type, label, options, required } = field;

    switch (type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <div key={name} className="space-y-2">
            <Label htmlFor={name}>{label} {required && '*'}</Label>
            <Input
              id={name}
              type={type}
              value={formData[name] || ''}
              onChange={(e) => handleFieldChange(name, e.target.value)}
              required={required}
              data-testid={`assessment-field-${name}`}
            />
          </div>
        );

      case 'date':
        return (
          <div key={name} className="space-y-2">
            <Label htmlFor={name}>{label} {required && '*'}</Label>
            <Input
              id={name}
              type="date"
              value={formData[name] || ''}
              onChange={(e) => handleFieldChange(name, e.target.value)}
              required={required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={name} className="space-y-2 col-span-2">
            <Label htmlFor={name}>{label}</Label>
            <Textarea
              id={name}
              value={formData[name] || ''}
              onChange={(e) => handleFieldChange(name, e.target.value)}
              rows={3}
            />
          </div>
        );

      case 'select':
        return (
          <div key={name} className="space-y-2">
            <Label>{label}</Label>
            <Select
              value={formData[name] || ''}
              onValueChange={(value) => handleFieldChange(name, value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {options?.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'checkbox':
        return (
          <div key={name} className="flex items-center space-x-2">
            <Checkbox
              id={name}
              checked={formData[name] || false}
              onCheckedChange={(checked) => handleFieldChange(name, checked)}
            />
            <Label htmlFor={name} className="font-normal">{label}</Label>
          </div>
        );

      case 'checkbox_group':
        return (
          <div key={name} className="space-y-3 col-span-2">
            <Label>{label}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {options?.map((opt) => (
                <div key={opt} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${name}-${opt}`}
                    checked={(formData[name] || []).includes(opt)}
                    onCheckedChange={(checked) => handleCheckboxGroupChange(name, opt, checked)}
                  />
                  <Label htmlFor={`${name}-${opt}`} className="font-normal text-sm">{opt}</Label>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div key={name} className="space-y-2">
            <Label htmlFor={name}>{label}</Label>
            <Input
              id={name}
              value={formData[name] || ''}
              onChange={(e) => handleFieldChange(name, e.target.value)}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/assessments')}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1e3a5f]">New Assessment</h1>
          <p className="text-slate-500">Create a new clinical assessment</p>
        </div>
      </div>

      {/* Assessment Setup */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="text-lg">Assessment Details</CardTitle>
          <CardDescription>Select patient, therapist and assessment type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Patient *</Label>
              <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                <SelectTrigger data-testid="assessment-patient-select">
                  <SelectValue placeholder="Select patient..." />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name} ({p.reg_no})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Therapist *</Label>
              <Select value={selectedTherapist} onValueChange={setSelectedTherapist}>
                <SelectTrigger data-testid="assessment-therapist-select">
                  <SelectValue placeholder="Select therapist..." />
                </SelectTrigger>
                <SelectContent>
                  {therapists.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Assessment Type *</Label>
              <Select value={assessmentType} onValueChange={setAssessmentType}>
                <SelectTrigger data-testid="assessment-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ot_initial">OT Initial Assessment</SelectItem>
                  <SelectItem value="pt_initial">PT Initial Assessment</SelectItem>
                  <SelectItem value="slt_initial">SLT Initial Assessment</SelectItem>
                  <SelectItem value="sensory_profile_2">Sensory Profile 2 (£300)</SelectItem>
                  <SelectItem value="psychological">Psychological Assessment</SelectItem>
                  <SelectItem value="hand_therapy">Hand Therapy Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entry Mode Selection - Upload Document OR Fill Form */}
      {selectedPatient && assessmentType && (
        <Card className="card-icrcl border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-blue-800">How would you like to create this assessment?</CardTitle>
            <CardDescription className="text-blue-600">
              Choose to upload a completed document or fill in the assessment form
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  uploadMode === 'upload' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 bg-white hover:border-green-300'
                }`}
                onClick={() => setUploadMode('upload')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    uploadMode === 'upload' ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    <Upload className={`w-5 h-5 ${uploadMode === 'upload' ? 'text-green-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Upload Completed Document</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Upload a completed assessment document (PDF, DOC, etc.)
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Will be marked as COMPLETE immediately
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  uploadMode === 'form' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 bg-white hover:border-blue-300'
                }`}
                onClick={() => setUploadMode('form')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    uploadMode === 'form' ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    <ClipboardList className={`w-5 h-5 ${uploadMode === 'form' ? 'text-blue-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Fill Assessment Form</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Complete the assessment using our digital form
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Can save as draft or submit when complete
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Upload Section */}
      {uploadMode === 'upload' && selectedPatient && assessmentType && (
        <Card className="card-icrcl">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Upload Assessment Document
            </CardTitle>
            <CardDescription>
              Upload a completed assessment document to mark this assessment as complete
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!uploadedFile ? (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">
                  Drag and drop your document here, or click to browse
                </p>
                <p className="text-sm text-slate-400 mb-4">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG (max 20MB)
                </p>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="assessment-doc-upload"
                />
                <Label htmlFor="assessment-doc-upload">
                  <Button type="button" variant="outline" className="cursor-pointer" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Select Document
                    </span>
                  </Button>
                </Label>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{uploadedFile.name}</p>
                      <p className="text-sm text-green-600">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setUploadedFile(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Uploading a document will automatically mark this assessment as <strong>COMPLETE</strong>.
                The document will be attached to the patient's record.
              </AlertDescription>
            </Alert>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate('/assessments')}>
                Cancel
              </Button>
              <Button 
                onClick={handleUploadSubmit}
                disabled={!uploadedFile || uploading}
                className="bg-green-600 hover:bg-green-700"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Create Complete Assessment
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assessment Form - only show if form mode selected */}
      {uploadMode === 'form' && template && (
        <Card className="card-icrcl">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#2a9d8f]" />
                {template.name}
              </CardTitle>
              <CardDescription>
                Complete all required sections of the assessment form
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.print()} className="rounded-full">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {template.sections && template.sections.length > 0 ? (
              <Tabs value={String(activeSection)} onValueChange={(v) => setActiveSection(Number(v))}>
                <ScrollArea className="w-full">
                  <TabsList className="w-full justify-start flex-nowrap overflow-x-auto mb-6">
                    {template.sections.map((section, idx) => (
                      <TabsTrigger 
                        key={idx} 
                        value={String(idx)}
                        className="whitespace-nowrap"
                      >
                        {section.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </ScrollArea>

                {template.sections.map((section, sectionIdx) => (
                  <TabsContent key={sectionIdx} value={String(sectionIdx)}>
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-[#1e3a5f] border-b pb-2">
                        {section.name}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.fields?.map((field) => renderField(field))}
                      </div>
                      
                      {/* Navigation buttons */}
                      <div className="flex justify-between pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                          disabled={activeSection === 0}
                        >
                          Previous Section
                        </Button>
                        {activeSection < template.sections.length - 1 ? (
                          <Button
                            onClick={() => setActiveSection(activeSection + 1)}
                            className="bg-[#2a9d8f] hover:bg-[#2a9d8f]/90"
                          >
                            Next Section
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={() => handleSubmit('draft')}
                              disabled={loading}
                            >
                              Save Draft
                            </Button>
                            <Button
                              onClick={() => handleSubmit('completed')}
                              disabled={loading}
                              className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                              data-testid="submit-assessment-btn"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Submit Assessment
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Loading form template...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit buttons when no template loaded yet */}
      {!template && selectedPatient && selectedTherapist && assessmentType && (
        <Card className="card-icrcl">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-slate-500">Loading assessment form...</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
